import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './useAuth';
import { useSocket } from '../contexts/SocketContext';

const API_BASE = `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/conversations`;

export const useConversations = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { user, token, hasPermission } = useAuth();
  const canViewMessages = hasPermission ? hasPermission('/messaging', 'View') : false;
  const {
    conversations,
    messageUnreadCount,
    setConversations,
    setMessageUnreadCount,
    markConversationRead: socketMarkConversationRead,
    upsertConversation,
  } = useSocket();

  const fetchConversations = useCallback(async (params = {}) => {
    if (!user || !token || !canViewMessages) {
      return { conversations: [] };
    }

    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(API_BASE, { params });
      setConversations(response.data.conversations || []);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to fetch conversations';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [user, token, canViewMessages, setConversations]);

  const fetchUnreadCount = useCallback(async () => {
    if (!user || !token || !canViewMessages) return 0;

    try {
      const response = await axios.get(`${API_BASE}/unread-count`);
      setMessageUnreadCount(response.data.count);
      return response.data.count;
    } catch (err) {
      console.error('Error fetching message unread count:', err);
      return 0;
    }
  }, [user, token, canViewMessages, setMessageUnreadCount]);

  const fetchConversationDetail = async (conversationId, params = {}) => {
    const response = await axios.get(`${API_BASE}/${conversationId}`, { params });
    return response.data;
  };

  const sendMessage = async ({ recipientId, objectType, objectId, body }) => {
    const payload = { recipientId, body };
    if (objectType && objectId) {
      payload.objectType = objectType;
      payload.objectId = objectId;
    }
    const response = await axios.post(API_BASE, payload);
    if (response.data.conversation) {
      upsertConversation(response.data.conversation);
    }
    return response.data;
  };

  const replyToConversation = async (conversationId, body) => {
    const response = await axios.post(`${API_BASE}/${conversationId}/messages`, { body });
    if (response.data.conversation) {
      upsertConversation(response.data.conversation);
    }
    return response.data;
  };

  const updateTaskStatus = async (conversationId, status) => {
    const response = await axios.patch(`${API_BASE}/${conversationId}/status`, { status });
    if (response.data.conversation) {
      upsertConversation(response.data.conversation);
    }
    return response.data;
  };

  const markAsRead = useCallback(async (conversationId) => {
    await axios.put(`${API_BASE}/${conversationId}/read`, {});
    socketMarkConversationRead(conversationId);
    await fetchUnreadCount();
  }, [socketMarkConversationRead, fetchUnreadCount]);

  const fetchEligibleRecipients = useCallback(async () => {
    const response = await axios.get(`${API_BASE}/eligible-recipients`);
    return {
      recipients: response.data.recipients || [],
      assistant: response.data.assistant || null,
    };
  }, [user, token]);

  const fetchAssistantThread = useCallback(async ({ objectType, objectId } = {}) => {
    const params = {};
    if (objectType && objectId) {
      params.objectType = objectType;
      params.objectId = objectId;
    }
    const response = await axios.get(`${API_BASE}/assistant-thread`, { params });
    if (response.data.conversation) {
      upsertConversation(response.data.conversation);
    }
    return response.data;
  }, [upsertConversation]);

  const findDirectConversation = useCallback(async (recipientId) => {
    const response = await axios.get(`${API_BASE}/direct/${recipientId}`);
    const conversation = response.data.conversation || null;
    if (conversation) {
      upsertConversation(conversation);
    }
    return conversation;
  }, [upsertConversation]);

  useEffect(() => {
    if (user && token && canViewMessages) {
      fetchConversations({ limit: 50 }).catch(console.error);
      fetchUnreadCount().catch(console.error);
    }
  }, [user, token, canViewMessages, fetchConversations, fetchUnreadCount]);

  return {
    conversations,
    messageUnreadCount,
    loading,
    error,
    canViewMessages,
    fetchConversations,
    fetchUnreadCount,
    fetchConversationDetail,
    sendMessage,
    replyToConversation,
    updateTaskStatus,
    markAsRead,
    fetchEligibleRecipients,
    fetchAssistantThread,
    findDirectConversation,
  };
};

export default useConversations;
